import { usePasswordResetCooldown, readPasswordResetCooldown } from "@vendistri/ui";
import { useEffect, useMemo, useState } from "react";
import {
  changePassword,
  requestPasswordReset,
  verifyCurrentPassword,
} from "./api";
import { ErrorMessage, normalizeIdentityEmail } from "../utils";
import { PasswordRequirements, passwordRules } from "./PasswordRequirements";
import { renderIcon } from "../icons";
import {
  IoCheckmarkCircle,
  IoCloseCircle,
  IoEye,
  IoEyeOff,
} from "react-icons/io5";
import { LoadingState } from "../vend/components/LoadingState";

type Mode = "change" | "reset";

type Props = {
  open: boolean;
  initialMode?: Mode;
  emailMode?: "editable" | "locked";
  initialEmail?: string;
  email?: string;
  onClose: () => void;
};

export function PasswordModal({
  open,
  initialMode = "change",
  emailMode = "editable",
  initialEmail,
  email: lockedEmail,
  onClose,
}: Props) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [requireCurrentPassword, setRequireCurrentPassword] = useState(false);
  const [requireResetEmail, setRequireResetEmail] = useState(false);
  const [sentEmail, setSentEmail] = useState<string | null>(null);
  const [changeStep, setChangeStep] = useState<"current" | "new">("current");
  const resetCooldown = usePasswordResetCooldown(email);

  const emailIssue = useMemo(() => {
    const msg = (error || "").toLowerCase();
    if (!msg) return false;
    return msg.includes("valid email") || msg.includes("email is required");
  }, [error]);

  useEffect(() => {
    if (!open) return;
    const nextEmail =
      emailMode === "locked" ? lockedEmail || "" : initialEmail || "";
    setEmail(nextEmail);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrentPwd(false);
    setShowNewPwd(false);
    setShowConfirmPwd(false);
    setBusy(false);
    setError(null);
    setInfo(null);
    setRequireCurrentPassword(false);
    setRequireResetEmail(false);
    setSentEmail(null);
    setChangeStep("current");
    const activeCooldown = readPasswordResetCooldown(nextEmail);
    setMode(activeCooldown && nextEmail.trim() ? "reset" : initialMode);
    setSentEmail(activeCooldown && nextEmail.trim() ? nextEmail.trim() : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMode]);

  useEffect(() => {
    if (mode === "change") {
      setChangeStep("current");
    }
  }, [mode]);


  const passwordValid = useMemo(() => {
    return passwordRules(newPassword).every((r) => r.valid);
  }, [newPassword]);

  const passwordsMatch = useMemo(() => {
    return (
      !!newPassword && !!confirmPassword && newPassword === confirmPassword
    );
  }, [newPassword, confirmPassword]);

  const canChange = useMemo(() => {
    return (
      !!currentPassword &&
      !!newPassword &&
      passwordsMatch &&
      passwordValid &&
      newPassword !== currentPassword
    );
  }, [currentPassword, newPassword, passwordsMatch, passwordValid]);

  useEffect(() => {
    if (mode !== "change") return;
    if (!error) return;
    const msg = error.toLowerCase();
    if (!msg.includes("invalid password")) return;
    setChangeStep("current");
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPwd(false);
    setShowConfirmPwd(false);
  }, [error, mode]);

  const close = () => {
    onClose();
  };

  const requestReset = async () => {
    const normalizedEmail = normalizeIdentityEmail(email);
    if (!normalizedEmail) {
      setRequireResetEmail(true);
      setError(null);
      setInfo(null);
      return;
    }
    if (resetCooldown.remainingSeconds > 0) return;
    setBusy(true);
    setRequireResetEmail(false);
    setError(null);
    setInfo(null);
    try {
      setEmail(normalizedEmail);
      await requestPasswordReset(normalizedEmail);
      setSentEmail(normalizedEmail);
      resetCooldown.start();
    } catch (e: any) {
      setError(e?.message || "Failed to send reset link");
    } finally {
      setBusy(false);
    }
  };

  const submitChange = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await changePassword({ currentPassword, newPassword });
      setInfo("Password updated.");
      setTimeout(() => close(), 800);
    } catch (e: any) {
      setError(e?.message || "Failed to update password");
    } finally {
      setBusy(false);
    }
  };

  const verifyAndContinue = async () => {
    if (!currentPassword.trim()) {
      setRequireCurrentPassword(true);
      setError(null);
      setInfo(null);
      return;
    }
    setBusy(true);
    setRequireCurrentPassword(false);
    setError(null);
    setInfo(null);
    try {
      await verifyCurrentPassword(currentPassword);
      setChangeStep("new");
    } catch (e: any) {
      setError(e?.message || "Failed to verify password");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const title = mode === "reset" ? "Reset Password" : "Change Password";
  const showCurrentPasswordRequired =
    mode === "change" &&
    changeStep === "current" &&
    requireCurrentPassword &&
    !currentPassword.trim();
  const resetCooldownSeconds = resetCooldown.remainingSeconds;
  const showResetSentState = mode === "reset" && Boolean(sentEmail || (email.trim() && resetCooldownSeconds > 0));
  const showResetEmailRequired = mode === "reset" && !showResetSentState && requireResetEmail && !email.trim();
  const resetCooldownLabel =
    resetCooldownSeconds > 0
      ? `Can resend in ${Math.floor(resetCooldownSeconds / 60)}:${String(resetCooldownSeconds % 60).padStart(2, "0")}`
      : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="app-zoom-modal bg-[rgb(var(--color-bg))] border border-[rgb(var(--color-border))] rounded-xl shadow-lg w-[92%] max-w-[34rem]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(var(--color-border))]">
          <h3 className="text-base font-semibold text-[rgb(var(--color-fg))] m-0">
            {title}
          </h3>
          <div className="ml-auto flex items-center justify-end gap-4">
            {showCurrentPasswordRequired || showResetEmailRequired ? (
              <span className="text-sm font-semibold text-red-500 text-right">
                Please fill this field.
              </span>
            ) : null}
            <button
              className="text-sm font-semibold text-[rgb(var(--color-muted))] hover:text-[rgb(var(--color-fg))]"
              onClick={close}
              disabled={busy}
            >
              Close
            </button>
          </div>
        </div>

        <div className="px-4 py-3 space-y-5">
          {mode === "reset" ? (
            <>
              {busy ? (
                <div className="py-6 flex justify-center">
                  <LoadingState label="Sending reset link…" size="md" />
                </div>
              ) : showResetSentState ? (
                <div className="py-4 space-y-4">
                  <div className="flex justify-center">
                    <div className="text-sm font-semibold text-emerald-600">
                      Success!
                    </div>
                  </div>
                  <div className="text-sm text-[rgb(var(--color-muted))]">
                    We sent a reset link to{" "}
                    <span className="font-semibold text-[rgb(var(--color-fg))]">
                      {sentEmail || email.trim()}
                    </span>
                    . Check your email and click the link to reset your
                    password.
                  </div>
                  <div className="text-xs italic text-[rgb(var(--color-muted))]">
                    Link expires in 30 minutes.
                  </div>
                  <div className="text-xs text-[rgb(var(--color-muted))]">
                    Didn&apos;t receive the email? Check your spam folder
                    <div className="mt-2">
                      <button
                        type="button"
                        className={`font-semibold ${
                          resetCooldownSeconds
                            ? "text-[rgb(var(--color-muted))]"
                            : "text-vend-blue"
                        }`}
                        onClick={requestReset}
                        disabled={busy || resetCooldownSeconds > 0}
                      >
                        {resetCooldownLabel || "Send another one"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-sm text-[rgb(var(--color-muted))]">
                    We’ll email you a secure link to reset your password.
                  </div>
                  <label className="flex flex-col gap-1 text-sm text-[rgb(var(--color-fg))]">
                    <span className="font-semibold uppercase text-[rgb(var(--color-muted))]">
                      Email
                    </span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (requireResetEmail && e.target.value.trim()) {
                          setRequireResetEmail(false);
                        }
                      }}
                      disabled={emailMode === "locked"}
                      aria-invalid={emailIssue || undefined}
                      className={`rounded-lg border bg-[rgb(var(--color-surface))] px-3 py-2 text-sm text-[rgb(var(--color-fg))] focus:outline-none focus:ring-2 ${
                        emailIssue || showResetEmailRequired
                          ? "border-red-500 focus:ring-red-500"
                          : "border-[rgb(var(--color-border))] focus:ring-vend-blue"
                      }`}
                    />
                  </label>
                </>
              )}
            </>
          ) : (
            <>
              <div className="text-sm text-[rgb(var(--color-muted))]">
                {changeStep === "current"
                  ? "Enter your current password to continue."
                  : "Choose your new password."}
              </div>

              {changeStep === "current" ? (
                <div className="relative">
                  <label className="flex flex-col gap-1 text-sm text-[rgb(var(--color-fg))]">
                    <span className="font-semibold uppercase text-[rgb(var(--color-muted))]">
                      Current Password
                    </span>
                    <input
                      type={showCurrentPwd ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        if (requireCurrentPassword && e.target.value.trim()) {
                          setRequireCurrentPassword(false);
                        }
                      }}
                      className={`w-full rounded-lg border bg-[rgb(var(--color-surface))] px-3 py-2 pr-10 text-sm text-[rgb(var(--color-fg))] focus:outline-none focus:ring-2 ${
                        showCurrentPasswordRequired
                          ? "border-red-500 focus:ring-red-500"
                          : "border-[rgb(var(--color-border))] focus:ring-vend-blue"
                      }`}
                    />
                  </label>
                  <button
                    type="button"
                    className="absolute right-2 top-8 text-[rgb(var(--color-muted))] hover:text-[rgb(var(--color-fg))]"
                    onClick={() => setShowCurrentPwd((v) => !v)}
                    aria-label={
                      showCurrentPwd ? "Hide password" : "Show password"
                    }
                  >
                    {renderIcon(showCurrentPwd ? IoEyeOff : IoEye, "h-5 w-5")}
                  </button>
                </div>
              ) : null}

              {changeStep === "new" ? (
                <>
                  <div className="relative">
                    <label className="flex flex-col gap-1 text-sm text-[rgb(var(--color-fg))]">
                      <span className="font-semibold uppercase text-[rgb(var(--color-muted))]">
                        New Password
                      </span>
                      <input
                        type={showNewPwd ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 pr-10 text-sm text-[rgb(var(--color-fg))] focus:outline-none focus:ring-2 focus:ring-vend-blue"
                      />
                    </label>
                    <button
                      type="button"
                      className="absolute right-2 top-8 text-[rgb(var(--color-muted))] hover:text-[rgb(var(--color-fg))]"
                      onClick={() => setShowNewPwd((v) => !v)}
                      aria-label={
                        showNewPwd ? "Hide password" : "Show password"
                      }
                    >
                      {renderIcon(showNewPwd ? IoEyeOff : IoEye, "h-5 w-5")}
                    </button>
                  </div>

                  <PasswordRequirements password={newPassword} />

                  <div className="relative">
                    <label className="flex flex-col gap-1 text-sm text-[rgb(var(--color-fg))]">
                      <span className="font-semibold uppercase text-[rgb(var(--color-muted))]">
                        Confirm Password
                      </span>
                      <input
                        type={showConfirmPwd ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 pr-10 text-sm text-[rgb(var(--color-fg))] focus:outline-none focus:ring-2 focus:ring-vend-blue"
                      />
                    </label>
                    <button
                      type="button"
                      className="absolute right-2 top-8 text-[rgb(var(--color-muted))] hover:text-[rgb(var(--color-fg))]"
                      onClick={() => setShowConfirmPwd((v) => !v)}
                      aria-label={
                        showConfirmPwd ? "Hide password" : "Show password"
                      }
                    >
                      {renderIcon(showConfirmPwd ? IoEyeOff : IoEye, "h-5 w-5")}
                    </button>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    {passwordsMatch ? (
                      <span className="text-emerald-600 inline-flex items-center gap-1">
                        {renderIcon(IoCheckmarkCircle, "h-4 w-4")}
                        Passwords match
                      </span>
                    ) : (
                      <span className="text-red-500 inline-flex items-center gap-1">
                        {renderIcon(IoCloseCircle, "h-4 w-4")}
                        Passwords must match
                      </span>
                    )}
                  </div>
                </>
              ) : null}

              {changeStep === "current" ? (
                <div className="text-xs text-[rgb(var(--color-muted))]">
                  Forgot your current password?{" "}
                  <button
                    type="button"
                    className="font-semibold text-vend-blue"
                    onClick={() => setMode("reset")}
                  >
                    Send a reset link
                  </button>
                  .
                </div>
              ) : null}
            </>
          )}

          {error ? <ErrorMessage message={error} /> : null}
          {info ? (
            <div className="text-sm text-[rgb(var(--color-muted))]">{info}</div>
          ) : null}
        </div>

        <div className="flex items-center justify-end px-4 py-3 border-t border-[rgb(var(--color-border))]">
          {mode === "reset" ? (
            showResetSentState ? (
              <button
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-vend-blue text-white"
                onClick={close}
              >
                Done
              </button>
            ) : (
              <button
                className={`px-4 py-2 text-sm font-semibold rounded-lg ${
                  true
                    ? "bg-vend-blue text-white"
                    : "bg-[rgb(var(--color-border))] text-[rgb(var(--color-muted))]"
                }`}
                disabled={busy}
                onClick={requestReset}
              >
                Send Reset Link
              </button>
            )
          ) : changeStep === "current" ? (
            <button
              className={`px-4 py-2 text-sm font-semibold rounded-lg ${
                true
                  ? "bg-vend-blue text-white"
                  : "bg-[rgb(var(--color-border))] text-[rgb(var(--color-muted))]"
              }`}
              disabled={busy}
              onClick={verifyAndContinue}
            >
              {busy ? "Verifying..." : "Continue"}
            </button>
          ) : (
            <button
              className={`px-4 py-2 text-sm font-semibold rounded-lg ${
                canChange
                  ? "bg-vend-blue text-white"
                  : "bg-[rgb(var(--color-border))] text-[rgb(var(--color-muted))]"
              }`}
              disabled={busy || !canChange}
              onClick={submitChange}
            >
              {busy ? "Updating..." : "Update Password"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
